import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useNowPlaying } from "../api/hooks.js";
import type { FeedNowPlaying } from "../api/now-playing-store.js";
import { apiUrl } from "../api/client.js";
import {
  describeMediaError,
  isMixedContentBlocked,
  pageProtocol,
} from "./stream.js";

/**
 * What the transport can be. There is no `paused`: a paused live stream is one that
 * keeps arriving into a buffer nobody hears, so this player stops — it drops the
 * connection — and that is why there is no seek bar either.
 */
export type PlaybackState =
  "idle" | "connecting" | "playing" | "stalled" | "error" | "blocked";

export interface PlayerTarget {
  feedId: string;
  feedName: string;
  streamUrl: string;
}

interface PlayerContextValue {
  target: PlayerTarget | null;
  state: PlaybackState;
  detail: string | null;
  volume: number;
  muted: boolean;
  /** Live state of whichever feed is being listened to, from that feed's own poller. */
  live: FeedNowPlaying;
  play: (target: PlayerTarget) => void;
  stop: () => void;
  toggle: (target: PlayerTarget) => void;
  setVolume: (volume: number) => void;
  toggleMuted: () => void;
}

const PlayerContext = createContext<PlayerContextValue | null>(null);

const VOLUME_KEY = "radio.volume";

function readStoredVolume(): number {
  try {
    const raw = window.localStorage.getItem(VOLUME_KEY);
    const value = raw === null ? Number.NaN : Number(raw);
    return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 1;
  } catch {
    return 1;
  }
}

export function PlayerProvider({ children }: { children?: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [target, setTarget] = useState<PlayerTarget | null>(null);
  const [state, setState] = useState<PlaybackState>("idle");
  const [detail, setDetail] = useState<string | null>(null);
  const [volume, setVolumeState] = useState(() =>
    typeof window === "undefined" ? 1 : readStoredVolume(),
  );
  const [muted, setMuted] = useState(false);
  const live = useNowPlaying(target?.feedId ?? null);

  const stop = useCallback(() => {
    const element = audioRef.current;
    if (element) {
      element.pause();
      // Dropping the source is what actually closes the connection to the broker.
      // Pausing alone leaves a live stream arriving into a buffer nobody hears.
      element.removeAttribute("src");
      element.load();
    }
    // The feed stays selected. Stopping is not deciding to listen to nothing, and a
    // player that emptied itself would make resuming a hunt back through the list.
    setState("idle");
    setDetail(null);
  }, []);

  const play = useCallback(
    (next: PlayerTarget) => {
      const element = audioRef.current;
      if (!element) return;
      if (isMixedContentBlocked(next.streamUrl, pageProtocol())) {
        element.pause();
        element.removeAttribute("src");
        setTarget(next);
        setState("blocked");
        setDetail(next.streamUrl);
        return;
      }
      setTarget(next);
      setState("connecting");
      setDetail(null);
      element.src = next.streamUrl;
      element.volume = volume;
      element.muted = muted;
      element.load();
      void element.play().catch((error: unknown) => {
        setState("error");
        setDetail(
          error instanceof Error && error.name === "NotAllowedError"
            ? "This browser needs a tap on play before it will start audio."
            : describeMediaError(element.error),
        );
      });
    },
    [muted, volume],
  );

  const toggle = useCallback(
    (next: PlayerTarget) => {
      const isCurrent = target?.feedId === next.feedId;
      if (isCurrent && state !== "idle") stop();
      else play(next);
    },
    [play, state, stop, target?.feedId],
  );

  // Transport state follows the element, not the button that was pressed: a stream
  // that stalls or drops says so on its own.
  useEffect(() => {
    const element = audioRef.current;
    if (!element) return;
    const onPlaying = () => {
      setState("playing");
      setDetail(null);
    };
    const onWaiting = () => {
      setState((current) => (current === "playing" ? "stalled" : current));
    };
    const onError = () => {
      setState("error");
      setDetail(describeMediaError(element.error));
    };
    const onEnded = () => {
      setState("error");
      setDetail("The stream ended.");
    };
    element.addEventListener("playing", onPlaying);
    element.addEventListener("waiting", onWaiting);
    element.addEventListener("stalled", onWaiting);
    element.addEventListener("error", onError);
    element.addEventListener("ended", onEnded);
    return () => {
      element.removeEventListener("playing", onPlaying);
      element.removeEventListener("waiting", onWaiting);
      element.removeEventListener("stalled", onWaiting);
      element.removeEventListener("error", onError);
      element.removeEventListener("ended", onEnded);
    };
  }, []);

  useEffect(() => {
    const element = audioRef.current;
    if (!element) return;
    element.volume = volume;
    element.muted = muted;
  }, [muted, volume]);

  const setVolume = useCallback((next: number) => {
    const clamped = Math.min(1, Math.max(0, next));
    setVolumeState(clamped);
    setMuted(clamped === 0);
    try {
      window.localStorage.setItem(VOLUME_KEY, String(clamped));
    } catch {
      // A refused storage is not a reason to refuse a volume change.
    }
  }, []);

  // The lock screen and the headset buttons are part of a phone's player. Metadata
  // is published for whatever is airing on whichever feed is being listened to.
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const session = navigator.mediaSession;
    const airing = live.data?.nowPlaying;
    if (!target || state === "idle") {
      session.metadata = null;
      session.playbackState = "none";
      return;
    }
    session.playbackState = state === "playing" ? "playing" : "paused";
    session.metadata = new MediaMetadata({
      title: airing?.title || target.feedName,
      artist: airing?.artist || target.feedName,
      album: target.feedName,
      artwork: airing?.artworkUrl
        ? [{ src: apiUrl(airing.artworkUrl), sizes: "512x512" }]
        : [],
    });
  }, [live.data?.nowPlaying, state, target]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const session = navigator.mediaSession;
    const resume = () => {
      if (target) play(target);
    };
    session.setActionHandler("play", resume);
    session.setActionHandler("pause", stop);
    session.setActionHandler("stop", stop);
    return () => {
      session.setActionHandler("play", null);
      session.setActionHandler("pause", null);
      session.setActionHandler("stop", null);
    };
  }, [play, stop, target]);

  const value = useMemo<PlayerContextValue>(
    () => ({
      target,
      state,
      detail,
      volume,
      muted,
      live,
      play,
      stop,
      toggle,
      setVolume,
      toggleMuted: () => {
        setMuted((current) => !current);
      },
    }),
    [detail, live, muted, play, setVolume, state, stop, target, toggle, volume],
  );

  return (
    <PlayerContext value={value}>
      {/*
        The one audio element in the product. It is rendered rather than constructed
        so its lifetime is the app's, and `preload="none"` because a live stream must
        not start arriving until someone asks for it.
      */}
      <audio ref={audioRef} preload="none" />
      {children}
    </PlayerContext>
  );
}

export function usePlayer(): PlayerContextValue {
  const value = use(PlayerContext);
  if (!value) throw new Error("usePlayer must be used inside PlayerProvider");
  return value;
}

/** Whether this feed is the one being listened to, and how it is going. */
export function useFeedPlayback(feedId: string): {
  isCurrent: boolean;
  state: PlaybackState;
} {
  const { target, state } = usePlayer();
  const isCurrent = target?.feedId === feedId;
  return { isCurrent, state: isCurrent ? state : "idle" };
}
